/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import IScene from "../../scene_interface";
import {TSkyParams} from "./lighting_settings_types";

export type TLighting = {
    sky: TSkyParams;
    headLightEnabled: boolean;
    sunLightOrientation: number;
    shadowIntensity: number;
    minimalDiffuseLight: number;
    sunLightEnabled: boolean;
    ambientEqualsDiffuse: boolean;
    headlightIntensity: number;
    ambientLightIntensity: number;
    sunLightElevation: number;
    sunLightIntensity: number
};

export default
class LightingSettings {
    protected settings: TLighting;
    private _stack: any[];

    constructor(aSceneAPI: IScene) {
        this.settings = {
            sunLightEnabled: false,
            headLightEnabled: true,
            headlightIntensity: 0.8,
            ambientLightIntensity: 0.2,
            ambientEqualsDiffuse: true,
            sunLightIntensity: 0.0, // rad
            sunLightOrientation: 0.0, // rad
            sunLightElevation: 1.5,
            minimalDiffuseLight: 0.0, // this is for shadow volumes "better" lighting of face which are not pointing toward sun
            shadowIntensity: 0.7,
            // sky shader
            sky: {
                turbidity: 4.5, // 10.0, (original default values)
                rayleigh: 1.683, // 2.0,
                mieCoefficient: 0.01, // 0.005,
                mieDirectionalG: 0.605, // 0.8,
                luminance: 1.01 // 1.0
            }
        };

        this._stack = [];
    }

    public push() {
        let item = {};
        for (let member in this.settings) {
            item[member] = this.settings[member];
        }
        this._stack.push(item);
    }

    public pop() {
        this.settings = this._stack[this._stack.length - 1];
        this._stack.splice(this._stack.length - 1, 1);
    }

    public setSunLightEnabled(aValue: boolean) {
        this.settings.sunLightEnabled = aValue;
    }

    public getSunLightEnabled(): boolean {
        return this.settings.sunLightEnabled;
    }

    public setHeadLightEnabled(aValue: boolean) {
        this.settings.headLightEnabled = aValue;
    }

    public getHeadLightEnabled(): boolean {
        return this.settings.headLightEnabled;
    }

    public setShadowIntensity(aValue: number) {
        this.settings.shadowIntensity = aValue;
    }

    public getShadowIntensity(): number {
        return this.settings.shadowIntensity;
    }

    public setHeadLightIntensity(aValue: number) {
        this.settings.headlightIntensity = aValue;
    }

    public getHeadLightIntensity(): number {
        return this.settings.headlightIntensity;
    }

    public setAmbientLightIntensity(aValue: number) {
        this.settings.ambientLightIntensity = aValue;
    }

    public getAmbientLightIntensity(): number {
        return this.settings.ambientLightIntensity;
    }

    public setMinimalDiffuseLight(aValue: number) {
        this.settings.minimalDiffuseLight = aValue;
    }

    public getMinimalDiffuseLight(): number {
        return this.settings.minimalDiffuseLight;
    }

    public setSunLightIntensity(aValue: number) {
        this.settings.sunLightIntensity = aValue;
    }

    public getSunLightIntensity(): number {
        return this.settings.sunLightIntensity;
    }

    public setAmbientEqualsDiffuse(aValue: boolean) {
        this.settings.ambientEqualsDiffuse = aValue;
    }

    public getAmbientEqualsDiffuse(): boolean {
        return this.settings.ambientEqualsDiffuse;
    }

    public setSunLightOrientation(aAngleRad: number) {
        this.settings.sunLightOrientation = aAngleRad;
    }

    public getSunLightOrientation(): number {
        return this.settings.sunLightOrientation;
    }

    public setSunLightElevation(aAngleRad: number) {
        this.settings.sunLightElevation = aAngleRad;
    }

    public getSunLightElevation(): number {
        return this.settings.sunLightElevation;
    }

    public setSkyParams(aSkyParams: TSkyParams) {
        this.settings.sky = aSkyParams;
    }

    public getSkyParams(): TSkyParams {
        return this.settings.sky;
    }
}
